import React, { useRef, useEffect, useState, useMemo } from "react";
import * as d3 from "d3";
import CommonModal from "../../Components/CommonModal";
import CommonTable from "../../Components/CommonTable";
import ShowVendorsDetailsData from "./ShowVendorsDetailsData";
import { third_party_monitored_vendors } from "../../services";
import { lightTheme } from "../../Constants/Colors";
import { SubHeading } from "../../Constants/Fonts";

const cmCharCode = ['A', 'B', 'C', 'D', 'F']

function VendorsByBusinessImpact({ data = {}, isWide = false, graphHeight = 500 }) {
    const containerRef = useRef(null);
    const svgRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [selectedDetails, setSelectedDetails] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showVendorDetails, setShowVendorDetails] = useState(false);
    const [vendorDetailsTargetOrgId, setVendorDetailsTargetOrgId] = useState(null);
    const [vendorDetailsName, setVendorDetailsName] = useState('');
    const [resolvingVendor, setResolvingVendor] = useState(false);

    // Track the container's own width via ResizeObserver (not just `window`
    // resize) so this redraws when ITS box changes size for any reason — a
    // sidebar toggling, a sibling panel resizing, first becoming visible —
    // not only when the browser window itself resizes. Ignore zero-width
    // readings; they happen mid-layout and would collapse the SVG.
    useEffect(() => {
        if (typeof ResizeObserver === "undefined") {
            if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
            return;
        }
        const ro = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect?.width;
            if (width) setContainerWidth(width);
        });
        if (containerRef.current) ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    // 🔹 Transform API heatmap data into flat array
    const transformedData = useMemo(() => {
        const rows = ["Critical", "High", "Medium", "Low"];
        const columns = [
            "Very Unlikely",
            "Unlikely",
            "Possible",
            "Likely",
            "Very Likely",
        ];

        const flatData = [];

        rows.forEach((row) => {
            columns.forEach((col) => {
                const value = data?.heatmap?.[row]?.[col] ?? 0; // read from heatmap
                flatData.push({ row, col, value });
            });
        });

        return flatData;
    }, [data]);

    // 🔹 Static color mapping per row + column
    const getCellColor = (row, col) => {
        if (col === "Very Unlikely") return "#19A07E";
        if (col === "Unlikely")
            return row === "Critical" || row === "High" ? "#D9BF21" : "#19A07E";
        if (col === "Possible")
            return row === "Critical" || row === "High" ? "#DE9E30" : "#D9BF21";
        if (col === "Likely")
            return row === "Critical" || row === "High" ? "#E56A49" : "#DE9E30";
        if (col === "Very Likely")
            return row === "Critical" || row === "High" ? "#D24646" : "#E56A49";
        return "#333";
    };

    // 🔹 Handle cell click → open modal with filtered details
    const handleCellClick = (row, col) => {
        const filtered = (data?.details || []).filter(
            (d) => d.impact_band === row && d.likelihood_band === col
        );
        setSelectedDetails(filtered);
        setShowModal(true);
    };

    // Draw heatmap
    useEffect(() => {
        if (!containerWidth || transformedData.length === 0) return;

        // left  — y-axis labels ("Critical" ~48px at 12px) + the rotated
        //          IMPACT title outside them.
        // bottom — x-axis labels wrap onto 2 lines (~42px) + the LIKELIHOOD
        //          title under them.
        const margin = { top: 60, right: 30, bottom: 76, left: 88 };
        const AXIS_TITLE_SIZE = 12;
        const totalHeight = graphHeight;
        const width = containerWidth;
        const height = totalHeight;

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        svg
            .attr("viewBox", `0 0 ${width} ${height}`)
            .attr("preserveAspectRatio", "xMidYMid meet");

        // Positioned below, once the centred grid origin is known.
        const chart = svg.append("g");

        const columns = [
            "Very Unlikely",
            "Unlikely",
            "Possible",
            "Likely",
            "Very Likely",
        ];
        const rows = ["Critical", "High", "Medium", "Low"];

        const gridWidth = width - margin.left - margin.right;
        const gridHeight = height - margin.top - margin.bottom;
        const cellSize = Math.min(gridWidth / columns.length, gridHeight / rows.length);
        const cellGap = 6; // whitespace between blocks

        // `cellSize` takes the SMALLER of the width- and height-derived sizes so
        // cells stay square. Whichever axis loses is left with slack, and the
        // grid used to absorb all of it on one side — pinned to the top-left of
        // the plot area with a band of empty card to the right. Centre it in the
        // slack instead.
        //
        // Centre the WHOLE labelled block — the label gutter (y-labels + the
        // rotated IMPACT title) plus the grid — not just the grid. Centring the
        // grid alone in the full width left the labels hanging off its left
        // edge, so the composition read as left-heavy with a dead band on the
        // right. Reserving `margin.left` for the gutter and balancing the outer
        // blank on both sides puts equal whitespace left and right:
        //   gridLeft - margin.left  ==  width - (gridLeft + gridPixelW)
        // `Math.max` keeps the gutter from clipping when the panel is too narrow
        // to centre (there it degrades to the old left-aligned layout).
        const gridPixelW = columns.length * cellSize;
        const gridPixelH = rows.length * cellSize;
        const gridLeft = Math.max(margin.left, (width + margin.left - margin.right - gridPixelW) / 2);
        const gridTop = margin.top + Math.max(0, (gridHeight - gridPixelH) / 2);

        chart.attr("transform", `translate(${gridLeft},${gridTop})`);

        // Draw cells
        rows.forEach((row, i) => {
            columns.forEach((col, j) => {
                const cellData = transformedData.find(
                    (d) => d.row === row && d.col === col
                );
                const value = cellData ? cellData.value : 0;

                chart
                    .append("rect")
                    .attr("x", j * cellSize + cellGap / 2)
                    .attr("y", i * cellSize + cellGap / 2)
                    .attr("width", cellSize - cellGap)
                    .attr("height", cellSize - cellGap)
                    .attr("rx", 6)
                    .attr("fill", getCellColor(row, col))
                    .style("cursor", "pointer")
                    .on("click", () => handleCellClick(row, col));

                chart
                    .append("text")
                    .attr("x", j * cellSize + cellSize / 2)
                    .attr("y", i * cellSize + cellSize / 2)
                    .attr("dy", ".35em")
                    .attr("text-anchor", "middle")
                    .attr("fill", "#fff")
                    .style("pointer-events", "none")
                    .style("font-size", "16px")
                    .style("font-weight", "700")
                    .text(value);
            });
        });

        // X/Y-axis labels — sized off the actual cell size this panel just
        // drew, not the whole window, so labels fit even when this panel is
        // squeezed narrower than the full viewport (e.g. sharing a row with
        // another panel). Clamped to the original 9-14px range.
        const fontSize = Math.max(9, Math.min(14, Math.round(cellSize * 0.16)));

        const xLabels = chart
            .selectAll(".x-label")
            .data(columns)
            .enter()
            .append("text")
            .attr("x", (d, i) => i * cellSize + cellSize / 2)
            .attr("y", rows.length * cellSize + 25)
            .attr("text-anchor", "middle")
            .attr("fill", lightTheme.textPrimary)
            .style("font-size", `${fontSize}px`)
            .each(function (d) {
                // "Very Unlikely"/"Very Likely" are long enough to collide with
                // the neighbouring column's label at typical cell widths — wrap
                // 2-word labels onto their own 2 lines instead of relying on
                // font size alone to make them fit.
                const words = d.split(" ");
                const label = d3.select(this);
                const x = label.attr("x");
                if (words.length > 1) {
                    label.append("tspan").attr("x", x).attr("dy", "0").text(words[0]);
                    label.append("tspan").attr("x", x).attr("dy", "1.1em").text(words.slice(1).join(" "));
                } else {
                    label.text(d);
                }
            });

        // Safety net for single-word columns ("Unlikely", "Possible", "Likely")
        // that have no space to wrap on: measure each label's actual rendered
        // width (not a guessed characters-per-pixel constant) and shrink just
        // that label's font further if it still doesn't fit its own column —
        // this is what stops it from bleeding into the next column's label at
        // narrow cell widths.
        const maxLabelWidth = cellSize - 4;
        xLabels.each(function () {
            const node = d3.select(this);
            const lines = node.selectAll("tspan").nodes();
            const widest = lines.length > 0
                ? Math.max(...lines.map((t) => t.getComputedTextLength()))
                : this.getComputedTextLength();
            if (widest > maxLabelWidth && widest > 0) {
                const scale = Math.max(0.5, maxLabelWidth / widest);
                node.style("font-size", `${Math.max(6, fontSize * scale)}px`);
            }
        });

        // Y-axis labels
        chart
            .selectAll(".y-label")
            .data(rows)
            .enter()
            .append("text")
            .attr("x", -10)
            .attr("y", (d, i) => i * cellSize + cellSize / 2)
            .attr("text-anchor", "end")
            .attr("alignment-baseline", "middle")
            .attr("fill", lightTheme.textPrimary)
            // .style("font-size", "14")
            .style("font-size", `${fontSize}px`)
            .text((d) => d);


        // Axis titles. The grid alone doesn't say what either axis measures —
        // "Critical/High/Medium/Low" and "Very Unlikely…Very Likely" are just
        // bare scales without them. Same treatment as the Risk Heatmap's
        // `.heatmap-impact-axis-label` / `.heatmap-likelihood-footer`: uppercase,
        // tracked out, muted, so they read as axis names and not as data.
        const axisTitle = (text) => svg
            .append("text")
            .attr("text-anchor", "middle")
            // textPrimary, not textMuted: at 12px with letter-spacing this
            // reads washed out in mid-grey, and the Risk Heatmap's own
            // IMPACT/LIKELIHOOD titles — the pattern this follows — are
            // primary text too. The uppercase and tracking are what separate
            // an axis title from the tick labels, not a lighter colour.
            .attr("fill", lightTheme.textPrimary)
            .style("font-size", `${AXIS_TITLE_SIZE}px`)
            .style("font-weight", 600)
            .style("letter-spacing", "0.1em")
            .text(text);

        // Rotated up the left edge, centred on the grid's own height.
        axisTitle("IMPACT")
            .attr("transform", `translate(${gridLeft - 72}, ${gridTop + gridPixelH / 2}) rotate(-90)`);

        // Under the wrapped column labels, centred on the grid's own width.
        axisTitle("LIKELIHOOD")
            .attr("x", gridLeft + gridPixelW / 2)
            .attr("y", gridTop + gridPixelH + 62);

        // Legend — spans exactly the grid, not the whole panel. The gradient
        // describes the columns, so a bar wider than the columns it explains
        // was both wrong and the main reason the grid looked off-centre.
        const legendWidth = gridPixelW;
        const legendHeight = 4;
        const legendX = gridLeft;

        const defs = svg.append("defs");
        const gradient = defs
            .append("linearGradient")
            .attr("id", "legend-gradient")
            .attr("x1", "0%")
            .attr("y1", "0%")
            .attr("x2", "100%")
            .attr("y2", "0%");

        const legendColors = [
            { offset: "0%", color: "#19A07E" },
            { offset: "25%", color: "#D9BF21" },
            { offset: "50%", color: "#DE9E30" },
            { offset: "75%", color: "#E56A49" },
            { offset: "100%", color: "#D24646" },
        ];

        legendColors.forEach((stop) => {
            gradient.append("stop").attr("offset", stop.offset).attr("stop-color", stop.color);
        });

        const legend = svg.append("g").attr("transform", `translate(${legendX}, ${20})`);
        legend
            .append("rect")
            .attr("width", legendWidth)
            .attr("height", legendHeight)
            .style("fill", "url(#legend-gradient)");
        legend
            .append("text")
            .attr("x", 0)
            .attr("y", -5)
            .attr("fill", lightTheme.textPrimary)
            .text("Low")
            .style("font-size", "14");

        legend
            .append("text")
            .attr("x", legendWidth)
            .attr("y", -5)
            .attr("text-anchor", "end")
            .attr("fill", lightTheme.textPrimary)
            .text("High")
            .style("font-size", "14");
    }, [transformedData, containerWidth, graphHeight]);

    // Table headers
    const headers = [
        "Vendor Name",
        "Likelihood",
        "Likelihood Score",
        "Impact",
        "Impact Score",
    ];

    // Table rows
    const rows = selectedDetails?.map((item) => ({
        data: [
            <div
                className="dark_blue_text cursor_pointer"
                style={{ opacity: resolvingVendor ? 0.5 : 1, pointerEvents: resolvingVendor ? 'none' : 'auto' }}
                onClick={async () => {
                    setResolvingVendor(true);
                    setVendorDetailsName(item?.vendor_name);
                    try {
                        const response = await third_party_monitored_vendors({ relation_id: item?.relation_id });
                        const vendors = response?.statusCode?.response_data?.data || [];
                        const matched = vendors.find(v => v.id === item?.relation_id || v.relation_id === item?.relation_id);
                        const targetOrgId = matched?.target_organization_id;
                        setVendorDetailsTargetOrgId(targetOrgId);
                        setShowVendorDetails(true);
                    } catch (e) {
                        console.error('Failed to resolve vendor:', e);
                    } finally {
                        setResolvingVendor(false);
                    }
                }}
            >
                {item?.vendor_name || "-"}
            </div>,
            item?.likelihood_band || "-",
            item?.likelihood_score || "-",
            item?.impact_band || "-",
            item?.impact_score || "-",
        ],
    }));

    return (
        <>
        <div className={`vendors-impact-graph cmn_elevated_card ${isWide ? "vendors-impact-graph_2" : ""}`} ref={containerRef}>
            <SubHeading as="h3" className="mb-3" color="var(--color-text-primary)">Vendors by Business Impact</SubHeading>
            <svg ref={svgRef} className="responsive-svg" />

            {/* Modal for details */}
            <CommonModal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="lg"
                title={`Vendor Details${selectedDetails?.length > 0 ? ` (${selectedDetails.length})` : ""}`}
            >
                {selectedDetails?.length > 0 ? (
                    <CommonTable headers={headers} rows={rows} noPadding noScroll />
                ) : (
                    <p className="text-center pt-5 mb-5 font_16">No Vendor Details Available.</p>
                )}
            </CommonModal>
        </div>

            {showVendorDetails &&
                <ShowVendorsDetailsData
                    show={showVendorDetails}
                    onHide={() => setShowVendorDetails(false)}
                    id={vendorDetailsTargetOrgId}
                    name={vendorDetailsName}
                />
            }
        </>
    );
}

export default VendorsByBusinessImpact;
